import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { formatDate } from '../lib/date';

// Component to handle map centering
function MapController({ center, zoom }) {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center, zoom, { animate: true });
    }
  }, [center, zoom, map]);

  return null;
}

// Photo labels for display
const PHOTO_LABELS = {
  sticker: 'Sticker',
  stationAndPlinth: 'Station & Plinth',
  loop1: 'Loop 1',
  loop2: 'Loop 2',
  loop3: 'Loop 3',
  loop4: 'Loop 4',
  loop5: 'Loop 5',
  loop6: 'Loop 6',
  Lane: 'Lane',
  Lane_320: 'Lane 320',
  layout: 'Layout',
  layoutOfAllLeftLanes: 'Layout - All Left Lanes'
};

function StationInfo() {
  const [allStations, setAllStations] = useState([]);
  const [filteredStations, setFilteredStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStation, setSelectedStation] = useState(null);
  const [stationImages, setStationImages] = useState({});
  const [stationAllPhotos, setStationAllPhotos] = useState({}); // All photos for each station
  const [mapCenter, setMapCenter] = useState([-29.8587, 31.0218]);
  const [mapZoom, setMapZoom] = useState(6);
  const [initialMapCenter, setInitialMapCenter] = useState([-29.8587, 31.0218]);
  const [initialMapZoom, setInitialMapZoom] = useState(6);
  const [mapKey, setMapKey] = useState(0);
  const [mapLayerType, setMapLayerType] = useState('standard'); // 'standard' or 'satellite'
  const tableRowRefs = useRef({});

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStation, setLightboxStation] = useState(null);
  const [lightboxPhotoIndex, setLightboxPhotoIndex] = useState(0);

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [routeFilter, setRouteFilter] = useState('');
  const [stratumFilter, setStratumFilter] = useState('');
  const [stationTypeFilter, setStationTypeFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchStations();
  }, []);

  useEffect(() => {
    // Apply filters and search whenever they change
    applyFiltersAndSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStations, searchTerm, ownerFilter, routeFilter, stratumFilter, stationTypeFilter, sortConfig]);

  const fetchStations = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/stations/all', { withCredentials: true });
      console.log('Stations data:', response.data);
      setAllStations(response.data);

      // Calculate center based on all markers
      if (response.data.length > 0) {
        const validStations = response.data.filter(s => s.LAT && s.LONG);
        if (validStations.length > 0) {
          const avgLat = validStations.reduce((sum, s) => sum + parseFloat(s.LAT), 0) / validStations.length;
          const avgLng = validStations.reduce((sum, s) => sum + parseFloat(s.LONG), 0) / validStations.length;
          const center = [avgLat, avgLng];
          setMapCenter(center);
          setInitialMapCenter(center);
          setInitialMapZoom(6);
          setMapZoom(6);
        }
      }
    } catch (err) {
      console.error('Error fetching stations:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...allStations];

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(station =>
        (station.STATION_NO?.toString().toLowerCase().includes(searchLower)) ||
        (station.Description?.toLowerCase().includes(searchLower)) ||
        (station.Owner?.toLowerCase().includes(searchLower)) ||
        (station.StationType?.toLowerCase().includes(searchLower)) ||
        (station.Route?.toLowerCase().includes(searchLower)) ||
        (station.Road?.toLowerCase().includes(searchLower)) ||
        (station.Stratum?.toLowerCase().includes(searchLower))
      );
    }

    // Apply owner filter
    if (ownerFilter) {
      filtered = filtered.filter(station => station.Owner === ownerFilter);
    }

    // Apply route filter
    if (routeFilter) {
      filtered = filtered.filter(station => station.Route === routeFilter);
    }

    // Apply stratum filter
    if (stratumFilter) {
      filtered = filtered.filter(station => station.Stratum === stratumFilter);
    }

    // Apply station type filter
    if (stationTypeFilter) {
      filtered = filtered.filter(station => station.StationType === stationTypeFilter);
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredStations(filtered);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleReset = () => {
    setSearchTerm('');
    setOwnerFilter('');
    setRouteFilter('');
    setStratumFilter('');
    setStationTypeFilter('');
    setSortConfig({ key: null, direction: 'asc' });
    setSelectedStation(null);
    setMapCenter(initialMapCenter);
    setMapZoom(initialMapZoom);
    setMapKey(prev => prev + 1);
  };

  const fetchStationImage = async (stationNo) => {
    // Check if we already have the image cached
    if (stationImages[stationNo]) {
      return;
    }

    try {
      const response = await axios.get(`/api/stations/${stationNo}/image`, { withCredentials: true });
      if (response.data && response.data.stationAndPlinth) {
        setStationImages(prev => ({
          ...prev,
          [stationNo]: response.data.stationAndPlinth
        }));
      }
    } catch (err) {
      console.error('Error fetching station image:', err);
    }

    // Also fetch all photos for the lightbox gallery
    if (!stationAllPhotos[stationNo]) {
      try {
        const photosResponse = await axios.get(`/api/locations/${stationNo}/photos`, { withCredentials: true });
        if (photosResponse.data && photosResponse.data.length > 0) {
          // Process photos into array format for gallery
          const photoArray = [];
          const photoRecord = photosResponse.data[0]; // Use first record

          Object.keys(PHOTO_LABELS).forEach(key => {
            if (photoRecord[key]) {
              photoArray.push({
                url: photoRecord[key],
                label: PHOTO_LABELS[key],
                key: key
              });
            }
          });

          setStationAllPhotos(prev => ({
            ...prev,
            [stationNo]: photoArray
          }));
        }
      } catch (err) {
        console.error('Error fetching all station photos:', err);
      }
    }
  };

  const openLightbox = (stationNo, startIndex = 0) => {
    setLightboxStation(stationNo);
    setLightboxPhotoIndex(startIndex);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxStation(null);
    setLightboxPhotoIndex(0);
  };

  const goToPrevPhoto = () => {
    const photos = stationAllPhotos[lightboxStation] || [];
    setLightboxPhotoIndex(prev => (prev - 1 + photos.length) % photos.length);
  };

  const goToNextPhoto = () => {
    const photos = stationAllPhotos[lightboxStation] || [];
    setLightboxPhotoIndex(prev => (prev + 1) % photos.length);
  };

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;

      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrevPhoto();
      if (e.key === 'ArrowRight') goToNextPhoto();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen, lightboxStation]);

  const handleMarkerClick = (station) => {
    setSelectedStation(station);
    fetchStationImage(station.STATION_NO);
    // Zoom map to clicked marker
    if (station.LAT && station.LONG) {
      const lat = parseFloat(station.LAT);
      const lng = parseFloat(station.LONG);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter([lat, lng]);
        setMapZoom(14);
      }
    }
    if (tableRowRefs.current[station.STATION_NO]) {
      tableRowRefs.current[station.STATION_NO].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const handleTableRowClick = (station) => {
    setSelectedStation(station);
    if (station.LAT && station.LONG) {
      const lat = parseFloat(station.LAT);
      const lng = parseFloat(station.LONG);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter([lat, lng]);
        setMapZoom(14);
      }
    }
  };

  const getMarkerColor = (owner) => {
    if (!owner) return '#3B82F6'; // Blue
    const ownerUpper = owner.toUpperCase();
    if (ownerUpper.includes('SANRAL')) {
      return '#EAB308'; // Yellow
    }
    return '#3B82F6'; // Blue for WCG
  };

  // Get unique values for filters
  const uniqueOwners = [...new Set(allStations.map(s => s.Owner).filter(Boolean))];
  const uniqueRoutes = [...new Set(allStations.map(s => s.Route).filter(Boolean))];
  const uniqueStationTypes = [...new Set(allStations.map(s => s.StationType).filter(Boolean))].sort();
  // eslint-disable-next-line no-unused-vars
  const uniqueStratums = [...new Set(allStations.map(s => s.Stratum).filter(Boolean))];

  // Create a pulsating marker icon for the selected station
  const createPulseIcon = (color) => {
    return L.divIcon({
      html: `
        <style>
          @keyframes station-pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.8;
            }
            50% {
              transform: scale(1.8);
              opacity: 0;
            }
          }
        </style>
        <div style="
          position: relative;
          width: 24px;
          height: 24px;
        ">
          <div style="
            position: absolute;
            top: 0; left: 0;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background-color: ${color};
            opacity: 0.4;
            animation: station-pulse 1.5s ease-in-out infinite;
          "></div>
          <div style="
            position: absolute;
            top: 4px; left: 4px;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background-color: ${color};
            border: 2px solid white;
            box-shadow: 0 2px 6px rgba(0,0,0,0.4);
          "></div>
        </div>
      `,
      className: '',
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12]
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className="h-full w-full overflow-hidden">
      {/* Search and Filters */}
      <div className="card p-3 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            {/* Search */}
            <div className="md:col-span-6 lg:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Search</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search stations..."
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <svg className="absolute right-3 top-2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Owner Filter */}
            <div className="md:col-span-2 lg:col-span-1">
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Owner</label>
              <select
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All</option>
                {uniqueOwners.map(owner => (
                  <option key={owner} value={owner}>{owner}</option>
                ))}
              </select>
            </div>

            {/* Route Filter */}
            <div className="md:col-span-2 lg:col-span-1">
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Route</label>
              <select
                value={routeFilter}
                onChange={(e) => setRouteFilter(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All</option>
                {uniqueRoutes.map(route => (
                  <option key={route} value={route}>{route}</option>
                ))}
              </select>
            </div>

            {/* Station Type Filter */}
            <div className="md:col-span-2 lg:col-span-1">
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Station Type</label>
              <select
                value={stationTypeFilter}
                onChange={(e) => setStationTypeFilter(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All</option>
                {uniqueStationTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Reset Button */}
            <div className="md:col-span-2 lg:col-span-1 flex items-end justify-end">
              <button
                onClick={handleReset}
                className="w-8 h-8 flex items-center justify-center bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-lg shadow-sm transition-all duration-200"
                title="Reset filters"
              >
                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

        <div className="mt-1.5">
          <div className="text-xs text-gray-600">
            Showing {filteredStations.length} of {allStations.length} stations
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-600">Loading stations...</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
          {/* Map Section */}
          <div className="card overflow-hidden flex flex-col order-2 lg:col-span-1 h-[calc(100vh-230px)] min-h-[400px]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Station Map</h3>
              {/* Map Legend */}
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-gray-700">WCG</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span className="text-gray-700">SANRAL</span>
                </div>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <MapContainer
                key={mapKey}
                center={mapCenter}
                zoom={mapZoom}
                style={{ height: '100%', width: '100%' }}
                className="rounded-lg"
              >
                {/* Conditionally render tile layer based on selected type */}
                {mapLayerType === 'standard' ? (
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                ) : (
                  <TileLayer
                    attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  />
                )}
                <MapController center={mapCenter} zoom={mapZoom} />

                {/* Map Layer Toggle Button */}
                <div className="leaflet-top leaflet-right" style={{ marginTop: '10px', marginRight: '10px', zIndex: 1000 }}>
                  <div className="leaflet-control">
                    <button
                      onClick={() => setMapLayerType(prev => prev === 'standard' ? 'satellite' : 'standard')}
                      className="bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-md shadow-md transition-all duration-200 flex items-center gap-2 px-3 py-2"
                      style={{ cursor: 'pointer' }}
                      title={mapLayerType === 'standard' ? 'Switch to Satellite View' : 'Switch to Standard View'}
                    >
                      {mapLayerType === 'standard' ? (
                        <>
                          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-medium text-gray-700">Satellite</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                          </svg>
                          <span className="text-sm font-medium text-gray-700">Standard</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {filteredStations.map((station) => {
                  if (!station.LAT || !station.LONG) return null;
                  const lat = parseFloat(station.LAT);
                  const lng = parseFloat(station.LONG);
                  if (isNaN(lat) || isNaN(lng)) return null;

                  const isSelected = selectedStation?.STATION_NO === station.STATION_NO;

                  return (
                    <CircleMarker
                      key={station.STATION_NO}
                      center={[lat, lng]}
                      radius={isSelected ? 10 : 8}
                      pathOptions={{
                        fillColor: getMarkerColor(station.Owner),
                        fillOpacity: 0.6,
                        color: getMarkerColor(station.Owner),
                        weight: isSelected ? 3 : 2,
                        opacity: 0.8
                      }}
                      eventHandlers={{
                        click: () => handleMarkerClick(station),
                      }}
                    >
                      <Popup>
                        <div className="text-sm" style={{ minWidth: '180px' }}>
                          <div className="mb-2 relative" style={{
                            width: '100%',
                            maxWidth: '200px',
                            height: '150px',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            backgroundColor: '#f3f4f6'
                          }}>
                            {stationImages[station.STATION_NO] ? (
                              <>
                                <img
                                  src={stationImages[station.STATION_NO]}
                                  alt={`Station ${station.STATION_NO}`}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => openLightbox(station.STATION_NO, 0)}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                                {/* Zoom button */}
                                <button
                                  onClick={() => openLightbox(station.STATION_NO, 0)}
                                  style={{
                                    position: 'absolute',
                                    bottom: '8px',
                                    right: '8px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px'
                                  }}
                                  title="View all photos"
                                >
                                  <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                  </svg>
                                  Gallery
                                </button>
                                {/* Photo count badge */}
                                {stationAllPhotos[station.STATION_NO] && stationAllPhotos[station.STATION_NO].length > 0 && (
                                  <span style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    backgroundColor: 'rgba(59, 130, 246, 0.9)',
                                    color: 'white',
                                    borderRadius: '9999px',
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: '600'
                                  }}>
                                    {stationAllPhotos[station.STATION_NO].length} photos
                                  </span>
                                )}
                              </>
                            ) : (
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: '#9ca3af'
                              }}>
                                <svg style={{ width: '48px', height: '48px', marginBottom: '8px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span style={{ fontSize: '12px' }}>No Image</span>
                              </div>
                            )}
                          </div>
                          <p className="font-semibold">Station {station.STATION_NO}</p>
                          <p className="text-gray-600">{station.Description || 'No description'}</p>
                          <p className="text-xs text-gray-500 mt-1">Owner: {station.Owner || 'Unknown'}</p>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}

                {/* Pulsating overlay for selected station */}
                {selectedStation && selectedStation.LAT && selectedStation.LONG && (() => {
                  const lat = parseFloat(selectedStation.LAT);
                  const lng = parseFloat(selectedStation.LONG);
                  if (isNaN(lat) || isNaN(lng)) return null;
                  return (
                    <Marker
                      position={[lat, lng]}
                      icon={createPulseIcon(getMarkerColor(selectedStation.Owner))}
                      interactive={false}
                    />
                  );
                })()}
              </MapContainer>
            </div>
          </div>

          {/* Table Section */}
          <div className="card overflow-hidden flex flex-col order-1 lg:col-span-2 h-[calc(100vh-230px)] min-h-[400px]">
            <h3 className="font-semibold text-gray-900 mb-4">Station Details</h3>
            <div className="overflow-auto flex-1">
              <table className="min-w-[600px] w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('STATION_NO')}
                    >
                      <div className="flex items-center gap-1">
                        Station No
                        {getSortIcon('STATION_NO')}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('Description')}
                    >
                      <div className="flex items-center gap-1">
                        Description
                        {getSortIcon('Description')}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('Owner')}
                    >
                      <div className="flex items-center gap-1">
                        Owner
                        {getSortIcon('Owner')}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('StationType')}
                    >
                      <div className="flex items-center gap-1">
                        Station Type
                        {getSortIcon('StationType')}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('Route')}
                    >
                      <div className="flex items-center gap-1">
                        Route
                        {getSortIcon('Route')}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('Road')}
                    >
                      <div className="flex items-center gap-1">
                        Road
                        {getSortIcon('Road')}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('Stratum')}
                    >
                      <div className="flex items-center gap-1">
                        Stratum
                        {getSortIcon('Stratum')}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('Lanes')}
                    >
                      <div className="flex items-center gap-1">
                        Lanes
                        {getSortIcon('Lanes')}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('LastVisit')}
                    >
                      <div className="flex items-center gap-1">
                        Last Site Visit
                        {getSortIcon('LastVisit')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStations.map((station) => (
                    <tr
                      key={station.STATION_NO}
                      ref={(el) => (tableRowRefs.current[station.STATION_NO] = el)}
                      onClick={() => handleTableRowClick(station)}
                      className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedStation?.STATION_NO === station.STATION_NO
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : ''
                      }`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {station.STATION_NO}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {station.Description || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            station.Owner?.toUpperCase().includes('SANRAL')
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {station.Owner || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {station.StationType || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {station.Route || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {station.Road || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {station.Stratum || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {station.Lanes || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {station.LastVisit ? formatDate(station.LastVisit) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Gallery Modal */}
      {lightboxOpen && lightboxStation && stationAllPhotos[lightboxStation] && (
        <div
          className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            title="Close (Esc)"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Station info header */}
          <div className="absolute top-4 left-4 text-white">
            <h3 className="text-xl font-semibold">Station {lightboxStation}</h3>
            <p className="text-sm text-gray-300">
              {stationAllPhotos[lightboxStation][lightboxPhotoIndex]?.label}
            </p>
          </div>

          {/* Photo counter */}
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 px-4 py-2 rounded-full">
            {lightboxPhotoIndex + 1} / {stationAllPhotos[lightboxStation].length}
          </div>

          {/* Previous button */}
          {stationAllPhotos[lightboxStation].length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); goToPrevPhoto(); }}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all"
              title="Previous (←)"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Main image */}
          <div
            className="max-w-5xl max-h-[80vh] mx-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={stationAllPhotos[lightboxStation][lightboxPhotoIndex]?.url}
              alt={stationAllPhotos[lightboxStation][lightboxPhotoIndex]?.label}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,' + encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
                    <rect fill="#374151" width="400" height="300"/>
                    <text fill="#9CA3AF" font-family="sans-serif" font-size="16" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">Image not available</text>
                  </svg>
                `);
              }}
            />
          </div>

          {/* Next button */}
          {stationAllPhotos[lightboxStation].length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); goToNextPhoto(); }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all"
              title="Next (→)"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Thumbnail strip */}
          {stationAllPhotos[lightboxStation].length > 1 && (
            <div
              className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 px-4 py-2 bg-black bg-opacity-50 rounded-lg overflow-x-auto max-w-[90vw]"
              onClick={(e) => e.stopPropagation()}
            >
              {stationAllPhotos[lightboxStation].map((photo, index) => (
                <button
                  key={photo.key}
                  onClick={() => setLightboxPhotoIndex(index)}
                  className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-all ${
                    index === lightboxPhotoIndex
                      ? 'border-blue-500 opacity-100'
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                  title={photo.label}
                >
                  <img
                    src={photo.url}
                    alt={photo.label}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </button>
              ))}
            </div>
          )}

          {/* Keyboard hint */}
          <div className="absolute bottom-4 right-4 text-gray-400 text-xs">
            Use ← → arrows to navigate • Esc to close
          </div>
        </div>
      )}
    </div>
  );
}

export default StationInfo;
